import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { RadioButtonField } from '@jutro/legacy/components';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WMICWizardChangeOrRenewalPage, useDocumentTitle, useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import {
    WMICRichTextUtil,
    LOBConstants,
    EMAIL_VALIDATION_STATUS,
    WMICAddressDetailsUtil,
    WMICContactDetailsUtil,
    WMICUserAccessUtil,
    WMICLogger,
} from 'wmic-pe-portals-utils-js';
import { WMICAddressDetails } from 'wmic-pe-components-platform-react';
import { AccountService } from 'wmic-pe-capability-gateway-policy';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';

import cx from 'classnames';

import { messages as commonMessages } from 'wmic-pe-capability-gateway-policychange-common-react';
import metadata from './WMICPolicyChangeContactDetailsPage.metadata.json5';
import styles from './WMICPolicyChangeContactDetailsPage.module.scss';
import messages from './WMICPolicyChangeContactDetailsPage.messages';

const STATES_WITH_PREFERRED_LANGUAGE_SELECTION = ['NB', 'NS', 'PE', 'QC'];
const STATES_WITH_PREFERRED_LANGUAGE_SELECTION_EXCEPT_AUTO = ['BC', 'AB', 'SK', 'MB', 'ON', 'YT'];

function WMICPolicyChangeContactDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const { wizardData: policyChangeVM, updateWizardData, updateWizardSnapshot } = props;
    const {
        registerInitialComponentValidation,
        registerComponentValidation,
        onValidate,
        initialValidation,
        isComponentValid,
    } = useValidation('WMICPolicyChangeContactDetailsPage');
    const { setWizardLoading } = useWizardModals();

    const { authUserData: currentUser, authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');

    const [initialAddress, setInitialAddress] = useState();
    const [isAddressVisible, setAddressVisible] = useState(false);
    const emailAddress1Value = _.get(policyChangeVM, 'baseData.primaryNamedInsured_WMIC.emailAddress1.value');
    const [originalEmailAddress, setOriginalEmailAddress] = useState(emailAddress1Value);
    const isPA = _.get(policyChangeVM, 'lob.value.code') === LOBConstants.PA_LINE;
    const [showErrors, setShowErrors] = useState(false);
    const EMAIL_VALIDATION_PATH_VALUE = 'baseData.emailValidationStatus_WMIC.value';
    const [isEditFormOpen, setEditFormOpen] = useState(false);
    const [emailValidationStatus, setEmailValidationStatus] = useState(
        policyChangeVM?.baseData?.emailValidationStatus_WMIC?.value?.code
    );

    const canEditPolicyChange = useMemo(
        () => WMICUserAccessUtil.canEditPolicyChange(currentUser.permission_Ext),
        [currentUser.permission_Ext]
    );

    useDocumentTitle(translator(messages.contactDetailsTitle), policyChangeVM);

    useEffect(() => {
        registerInitialComponentValidation(
            () =>
                !_.isEmpty(emailAddress1Value) && _.get(policyChangeVM, 'baseData.periodStatus.value.code') === 'Quoted'
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        registerComponentValidation(() =>
            WMICContactDetailsUtil.checkEmailForDigitalorEPOI(policyChangeVM, emailAddress1Value)
        );
    }, [registerComponentValidation, policyChangeVM, emailAddress1Value]);

    useEffect(() => {
        if (originalEmailAddress !== emailAddress1Value) {
            _.set(policyChangeVM, EMAIL_VALIDATION_PATH_VALUE, EMAIL_VALIDATION_STATUS.DRAFT);
            setEmailValidationStatus(EMAIL_VALIDATION_STATUS.DRAFT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailAddress1Value, originalEmailAddress]);

    const toggleAddressVisible = useCallback(
        addressWillBeVisible => {
            if (addressWillBeVisible) {
                const policyAddress = _.get(policyChangeVM, 'baseData.policyAddress.value');
                const addressOnOpen = _.cloneDeep(policyAddress);
                if (!_.isNil(addressOnOpen)) {
                    setInitialAddress(addressOnOpen);
                }
            }
            setAddressVisible(addressWillBeVisible);
            setEditFormOpen(addressWillBeVisible);
        },
        [setAddressVisible, policyChangeVM]
    );

    const areAddressFieldsValid = useCallback(() => {
        const addressVM = _.get(policyChangeVM, 'baseData.policyAddress');

        if (!addressVM) {
            return true;
        }

        return addressVM.aspects && addressVM.aspects.valid && addressVM.aspects.subtreeValid;
    }, [policyChangeVM]);

    const onCancelContactDetails = useCallback(() => {
        if (!_.isNil(initialAddress)) {
            _.set(policyChangeVM, 'baseData.policyAddress.value', initialAddress);
        }
        _.set(policyChangeVM, 'isEditingPolicyChangeContactDetails.value', false);
        updateWizardSnapshot(policyChangeVM);
        updateWizardData(policyChangeVM);
        toggleAddressVisible(false);
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyChangeVM, toggleAddressVisible, updateWizardSnapshot]);

    const handleSaveAddress = useCallback(async () => {
        if (areAddressFieldsValid()) {
            setWizardLoading(true, translator(messages.updatingContactDetails));
            try {
                const policyAddress = _.get(policyChangeVM, 'baseData.policyAddress');
                policyAddress.displayName = WMICAddressDetailsUtil.formatAddress(policyAddress);
                _.set(policyChangeVM, 'baseData.policyAddress', policyAddress);

                policyChangeVM.value = await EndorsementService.saveEndorsement([policyChangeVM.value], authHeader);
                _.set(policyChangeVM, 'isEditingPolicyChangeContactDetails.value', false);
                updateWizardSnapshot(policyChangeVM);
                updateWizardData(policyChangeVM);
                setAddressVisible(false);
                setEditFormOpen(false);
            } catch (err) {
                WMICLogger.error('Save address failed', err);
                throw new Error(err);
            } finally {
                setWizardLoading(false);
            }
        }
    }, [
        areAddressFieldsValid,
        setWizardLoading,
        translator,
        policyChangeVM,
        EndorsementService,
        authHeader,
        onCancelContactDetails,
    ]);

    const validateEmail = useCallback(() => {
        if (
            emailAddress1Value === _.get(policyChangeVM, 'baseData.primaryNamedInsured_WMIC.confirmEmailAddress.value')
        ) {
            AccountService.validateEmail(emailAddress1Value, _.get(policyChangeVM, 'jobID.value'), authHeader)
                .then(res => {
                    _.set(policyChangeVM, EMAIL_VALIDATION_PATH_VALUE, res.emailValidationStatus);
                    setEmailValidationStatus(res.emailValidationStatus);
                    setOriginalEmailAddress(emailAddress1Value);
                    if (isPA) {
                        const drivers = _.get(policyChangeVM, 'lobData.personalAuto.coverables.drivers.value');
                        const primaryNamedDriverIndex = drivers.findIndex(
                            driver => driver.isPrimaryInsured_WMIC === true
                        );
                        if (primaryNamedDriverIndex !== -1) {
                            _.set(
                                policyChangeVM,
                                `lobData.personalAuto.coverables.drivers.value[${primaryNamedDriverIndex}].emailAddress1`,
                                emailAddress1Value
                            );
                            _.set(
                                policyChangeVM,
                                `lobData.personalAuto.coverables.drivers.value[${primaryNamedDriverIndex}].confirmDriverEmailAddress_WMIC`,
                                emailAddress1Value
                            );
                        }
                    }
                })
                .catch(() => {
                    _.set(policyChangeVM, EMAIL_VALIDATION_PATH_VALUE, EMAIL_VALIDATION_STATUS.ERROR);
                    setEmailValidationStatus(EMAIL_VALIDATION_STATUS.ERROR);
                });
        }
    }, [authHeader, emailAddress1Value, isPA, policyChangeVM]);

    const getValidationStatus = useCallback(() => {
        // eslint-disable-next-line default-case
        switch (emailValidationStatus) {
            case EMAIL_VALIDATION_STATUS.VALID:
                return messages.emailValid;
            case EMAIL_VALIDATION_STATUS.INVALID_TLS:
                return messages.invalidtls;
            case EMAIL_VALIDATION_STATUS.ERROR:
                return messages.unavailable;
            case EMAIL_VALIDATION_STATUS.INVALID:
                return messages.invalid;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailValidationStatus]);

    const onEditContactDetails = () => {
        _.set(policyChangeVM, 'isEditingPolicyChangeContactDetails.value', true);
        setShowErrors(false);
        updateWizardData(policyChangeVM);
        toggleAddressVisible(!isAddressVisible);
    };

    const polDocPrefLangVisible =
        STATES_WITH_PREFERRED_LANGUAGE_SELECTION.includes(_.get(policyChangeVM, 'baseData.baseState.value.code')) ||
        (!isPA &&
            STATES_WITH_PREFERRED_LANGUAGE_SELECTION_EXCEPT_AUTO.includes(
                _.get(policyChangeVM, 'baseData.baseState.value.code')
            ));

    const overrideProps = {
        '@field': {
            parentNode: policyChangeVM,
            showRequired: true,
            readOnly: !canEditPolicyChange,
        },

        edeliveryEnrolled_WMIC: {
            visible: _.get(policyChangeVM, 'baseData.edeliveryEnrolled_WMIC.aspects.ocular'),
        },

        polDocPrefLang_WMIC: {
            visible: polDocPrefLangVisible,
        },
        epoiContainer: {
            visible: isPA,
        },
        statusIcon: {
            icon: _.get(policyChangeVM, 'baseData.isBrokerOwned_WMIC.value', false) ? 'gw-info' : 'mi-warning',
        },
        statusMessageBody: {
            content: _.get(policyChangeVM, 'baseData.isBrokerOwned_WMIC.value', false)
                ? translator(messages.brokerDistributed)
                : WMICRichTextUtil.translateRichText(translator(messages.consent)),
        },
        deliveryOfDocumentsContainer: {
            visible: _.get(policyChangeVM, 'baseData.edeliveryEnrolled_WMIC.aspects.ocular'),
        },
        addressContainer: {
            visible: isAddressVisible,
        },
        editItalicize: {
            onClick: onEditContactDetails,
        },
        policyAddress: {
            readOnly: true,
        },
        policyDetailsPageAddressComponent: {
            addressVM: _.get(policyChangeVM, 'baseData.policyAddress', {}),
            onValidate,
            showErrors,
            hideBorder: false,
            hideButtons: false,
            readOnlyCountry: true,
            onSave: handleSaveAddress,
            onCancel: onCancelContactDetails,
        },
        accountEmailContainer: {
            visible:
                _.get(policyChangeVM, 'baseData.edeliveryEnrolled_WMIC.value', false) ||
                _.get(policyChangeVM, 'lobData.personalAuto.epoiEnrolled_WMIC.value', false),
        },
        confirmEmail: {
            visible: originalEmailAddress !== emailAddress1Value,
        },
        emailValidationIcon: {
            visible: emailValidationStatus === EMAIL_VALIDATION_STATUS.VALID,
        },
        emailValidationStatus: {
            visible: !_.isNil(getValidationStatus()),
            content: translator(getValidationStatus()),
            className: cx(styles.gwAlert, {
                [styles.error]: emailValidationStatus !== EMAIL_VALIDATION_STATUS.VALID,
            }),
        },
        editAddress: {
            visible: canEditPolicyChange,
        },
        verifyEmailButton: {
            visible: canEditPolicyChange,
        },
        verifyEmailContainer: {
            visible: emailValidationStatus !== EMAIL_VALIDATION_STATUS.VALID,
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            WMICAddressDetails,
            RadioButtonField,
        },
        resolveCallbackMap: {
            verifyEmail: validateEmail,
        },
    };

    const onNext = useCallback(async () => {
        try {
            if (isAddressVisible || !isComponentValid) {
                setShowErrors(true);
                return false;
            }
            setWizardLoading(true, translator(commonMessages.savingTransactionDetails));
            const newPolicyChangeVM = viewModelService.clone(policyChangeVM);
            _.unset(newPolicyChangeVM.value, 'bindData');

            policyChangeVM.value = await EndorsementService.saveEndorsement([newPolicyChangeVM.value], authHeader);
            updateWizardData(policyChangeVM);
            return policyChangeVM;
        } finally {
            setWizardLoading(false);
        }

        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        EndorsementService,
        authHeader,
        isAddressVisible,
        isComponentValid,
        policyChangeVM,
        setWizardLoading,
        translator,
        viewModelService,
    ]);

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
            disableNext={isEditFormOpen}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyChangeVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPolicyChangeContactDetailsPage.propTypes = wizardProps;
export default WMICPolicyChangeContactDetailsPage;
